import { FC, PropsWithChildren, ReactNode } from 'react';
import { useControllableValue } from 'ahooks';

import { SizeType } from 'antd/es/config-provider/SizeContext';
import { ROUTER_PATHNAME } from 'common/router/config';

import { CloseOutlined } from '@ant-design/icons';
import { Modal, Flex, Typography, Button, ModalProps, ConfigProvider } from '@fuxi/eevee-ui';
import { SecondaryButton } from 'common/components';

export type TiangongModalProps = Omit<ModalProps, 'footer' | 'title'> & {
  size?: SizeType;
  title?: string;
  footer?: ReactNode;
  bodyClassNames?: string;
  onOpenChange?: (open: boolean) => void;
};

enum ContentWidth {
  small = 400,
  middle = 520,
  large = 678,
}
export const TiangongModal: FC<PropsWithChildren<TiangongModalProps>> = ({
  open,
  size = 'middle',
  children,
  title,
  closeIcon,
  closable = true,
  onOk,
  okText = '确认',
  okType,
  okButtonProps,
  onCancel,
  onOpenChange,
  cancelText = '取消',
  cancelButtonProps,
  footer,
  bodyClassNames,
  ...props
}) => {
  const [opened, setOpened] = useControllableValue(
    { open, onOpenChange },
    { valuePropName: 'open', trigger: 'onOpenChange' },
  );

  const onClose = (e) => {
    onCancel?.(e);
    setOpened(false);
  };

  return (
    <ConfigProvider
      theme={{
        components: {
          Table: {
            headerBg: 'rgba(31, 31, 46, 1)',
          },
        },
      }}>
      <Modal
        centered
        open={opened}
        onOk={onOk}
        title={null}
        footer={null}
        closeIcon={false}
        onCancel={onClose}
        width={ContentWidth[size]}
        classNames={{ wrapper: 'py-xxl', content: 'p-lg! border-radius-xl!' }}
        styles={{
          mask:
            location.pathname.includes(ROUTER_PATHNAME.TASK) || location.pathname.includes(ROUTER_PATHNAME.MY_BIDDING)
              ? undefined
              : { backdropFilter: 'blur(10px)' },
        }}
        {...props}>
        <div className={bodyClassNames}>
          {/* Modal Header */}
          <Flex justifyBetween className="mb-lg">
            <div className="w-28px" />
            <Typography.Title level={3} className="mb-0!">
              {title}
            </Typography.Title>
            {closable ? (
              <CloseOutlined className="font-s-heading-3 mr-xxs" onClick={onClose} />
            ) : (
              <div className="w-28px"></div>
            )}
          </Flex>

          {children}

          {/* Modal Footer */}
          {footer !== undefined ? (
            footer
          ) : (
            <Flex justifyCenter gap={20} className="mt-lg">
              <SecondaryButton
                size="large"
                shape="round"
                onClick={onClose}
                className="w-240px"
                children={cancelText}
                {...cancelButtonProps}
              />
              <Button
                size="large"
                shape="round"
                type="primary"
                onClick={onOk}
                children={okText}
                className="w-240px"
                {...okButtonProps}
              />
            </Flex>
          )}
        </div>
      </Modal>
    </ConfigProvider>
  );
};
